import React, { useState } from "react";
import { X, Send, Loader2, Mail } from "lucide-react";
import toast from "react-hot-toast";
import { sendOfferToVendor } from "../../../api/propertyManagement";

const SendToVendorModal = ({
  isOpen,
  onClose,
  offer,
  vendorEmail,
  vendorName,
  vendorPendingActivation,
  onSent,
}) => {
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen || !offer) return null;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await sendOfferToVendor(offer.id, message);
      const data = res?.data || {};
      toast.success("Sent to vendor");
      if (data.email_sent === false) {
        toast.error("Vendor email failed to send (will retry)", { duration: 4500 });
      }
      onSent?.(data);
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to send to vendor";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="px-6 py-5 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-[#55C5D0]/10">
              <Send className="w-5 h-5 text-[#55C5D0]" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Send Offer to Vendor</h3>
              <p className="text-xs text-gray-500 mt-0.5">Ref: {offer.reference_number}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="rounded-xl border border-[#55C5D0]/20 bg-[#55C5D0]/5 p-4 flex items-start gap-3">
            <Mail className="w-5 h-5 text-[#55C5D0] mt-0.5" />
            <div className="text-sm text-gray-700">
              <p className="font-semibold">
                {vendorName || vendorEmail || "Primary vendor"}
              </p>
              {vendorEmail && (
                <p className="text-xs text-gray-500 mt-0.5">{vendorEmail}</p>
              )}
              {vendorPendingActivation && (
                <p className="text-[11px] text-amber-600 mt-1.5 font-semibold uppercase tracking-wide">
                  Vendor portal invite pending — they'll see the offer after activation
                </p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
              Message to vendor (optional)
            </label>
            <textarea
              rows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add any context or recommendation for the vendor before they review..."
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/20 focus:border-[#55C5D0] transition-all resize-none placeholder:text-gray-400"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              disabled={submitting}
              className="flex-1 py-3 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-all disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-1 py-3 rounded-xl bg-[#55C5D0] text-white text-sm font-semibold hover:bg-[#4AADB7] transition-all disabled:opacity-50 shadow-lg shadow-[#55C5D0]/25 flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" /> Send to Vendor
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendToVendorModal;
